import React, { ReactNode } from 'react';
import { Calendar, Settings, User, X } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { destructiveIconBtnClass, editControlsPanelClass, widgetShellClass } from '../themeHelpers';

export interface DashboardFilterSupportProp {
  timeRange?: boolean;
  scope?: boolean;
}

export interface DraggableWidgetProps {
  id: string;
  title: string;
  children: ReactNode;
  isEditing?: boolean;
  onRemove?: () => void;
  onSettingsClick?: () => void;
  dragHandle?: ReactNode;
  /** When set, show icon(s): calendar for Time range, person for Scope (My work / Team) */
  dashboardFilterSupport?: DashboardFilterSupportProp | null;
}

const DraggableWidget: React.FC<DraggableWidgetProps> = ({
  id,
  title,
  children,
  isEditing = false,
  onRemove,
  onSettingsClick,
  dragHandle,
  dashboardFilterSupport,
}) => {
  const { colors } = useTheme();
  const usesTimeRange = dashboardFilterSupport?.timeRange;
  const usesScope = dashboardFilterSupport?.scope;
  const showFilterBadge = usesTimeRange || usesScope;

  return (
    <div className={widgetShellClass(colors)} data-widget-id={id}>
      {isEditing && (dragHandle || onRemove || onSettingsClick) && (
        <div className={editControlsPanelClass(colors)}>
          {showFilterBadge && (
            <span
              className={cn(
                'mr-0.5 flex min-h-[44px] items-center gap-1 border-r pr-1.5 sm:min-h-0',
                colors.border,
                colors.primaryFg,
              )}
              title={[usesTimeRange && 'Time range', usesScope && 'Scope'].filter(Boolean).join(', ')}
            >
              {usesTimeRange && <Calendar className="h-3.5 w-3.5" aria-hidden />}
              {usesScope && <User className="h-3.5 w-3.5" aria-hidden />}
            </span>
          )}
          {dragHandle && (
            <span
              className={cn(
                'drag-handle flex min-h-[44px] min-w-[44px] cursor-grab touch-none items-center justify-center rounded-lg p-2.5 active:cursor-grabbing sm:min-h-0 sm:min-w-0 sm:p-1.5',
                colors.textMuted,
                colors.interactiveHover,
              )}
            >
              {dragHandle}
            </span>
          )}
          {onSettingsClick && (
            <button
              type="button"
              onClick={onSettingsClick}
              className={cn(
                'flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2.5 transition-colors sm:min-h-0 sm:min-w-0 sm:p-1.5',
                colors.textMuted,
                colors.primaryFg,
                colors.interactiveHover,
              )}
              title="Widget settings"
              aria-label={`Settings for ${title}`}
            >
              <Settings className="h-4 w-4" aria-hidden />
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className={cn(
                'flex min-h-[44px] min-w-[44px] items-center justify-center p-2.5 sm:min-h-0 sm:min-w-0 sm:p-1.5',
                destructiveIconBtnClass(colors),
              )}
              title="Remove widget"
              aria-label={`Remove ${title}`}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-3 sm:p-3.5">
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </div>
    </div>
  );
};

export default DraggableWidget;
